/* Time-based subscription enforcement.
   Reads expirationDate + gracePeriodUntil from the org doc.
   When Stripe/M-PESA detection is live, those webhooks write the same fields
   and this hook picks it up automatically — no change needed here. */
import { useEffect, useState } from 'react';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';

export type SubStatus = 'loading' | 'active' | 'grace' | 'locked';

export interface OrgSubscription {
  status: SubStatus;
  daysLeft: number;       // positive = days until expiry; negative = days past expiry
  daysInGrace: number;    // how many days into the grace window (0 if not in grace)
  graceEnds: Date | null;
  expirationDate: Date | null;
  isPilot: boolean;
  /** Premium features (Analytics, school-trip planning, full Fuel) unlock for
   *  any active org on a real plan — including during the free pilot, since the
   *  card is already saved and billed when the pilot ends. */
  isPremiumUnlocked: boolean;
  /** Days left in the pilot (0 when not a pilot). */
  pilotDaysLeft: number;
  /** The org's plan id ('pilot'|'basic'|'premium'|'enterprise'|''). */
  plan: string;
  /** Zana Mode is Premium/Enterprise ONLY — including during pilot. */
  isZanaTier: boolean;
  /** A card charge failed and we're in the grace window → show the "retry / pay with
   *  M-Pesa" banner. Distinct from the pre-expiry warning. */
  paymentPending: boolean;
}

const GRACE_DAYS = 2;

const LOADING: OrgSubscription = {
  status: 'loading', daysLeft: 0, daysInGrace: 0, graceEnds: null,
  expirationDate: null, isPilot: false, isPremiumUnlocked: false, pilotDaysLeft: 0, paymentPending: false,
  plan: '', isZanaTier: false,
};

export function useOrgSubscription(orgCode: string | null): OrgSubscription {
  const [sub, setSub] = useState<OrgSubscription>(LOADING);

  useEffect(() => {
    if (!orgCode) {
      // Unknown org → fail open (active + unlocked), never gate.
      setSub({ ...LOADING, status: 'active', isPremiumUnlocked: true });
      return;
    }
    const q = query(collection(db, 'organizations'), where('code', '==', orgCode), limit(1));
    const unsub = onSnapshot(q, (snap) => {
      const d = snap.docs[0]?.data();
      // The plan tier drives Zana gating (Premium/Enterprise only).
      const plan = (d?.subscriptionPlan || d?.plan || '').toString().trim().toLowerCase();

      // Derive the premium fields from a core result and emit once.
      const emit = (core: Omit<OrgSubscription, 'isPremiumUnlocked' | 'pilotDaysLeft' | 'paymentPending' | 'plan' | 'isZanaTier'>, paymentPending = false) =>
        setSub({
          ...core,
          // Full plan features during pilot (product decision 2026-08-21).
          isPremiumUnlocked: core.status !== 'locked',
          pilotDaysLeft: core.isPilot ? Math.max(0, core.daysLeft) : 0,
          paymentPending,
          plan,
          // Premium/Enterprise + not locked → Zana on (pilots included).
          isZanaTier: (plan === 'premium' || plan === 'enterprise') && core.status !== 'locked',
        });

      if (!d) {
        emit({ status: 'active', daysLeft: 9999, daysInGrace: 0, graceEnds: null, expirationDate: null, isPilot: false });
        return;
      }

      // Orgs that opted out of enforcement (manually set by PEAR admin) skip all checks.
      if (d.subscriptionExempt === true) {
        emit({ status: 'active', daysLeft: 9999, daysInGrace: 0, graceEnds: null, expirationDate: null, isPilot: !!d.isPilot });
        return;
      }

      // Normalise Firestore Timestamps → JS Date.
      const toDate = (v: unknown): Date | null => {
        if (!v) return null;
        if (typeof (v as { toDate?: () => Date }).toDate === 'function') return (v as { toDate: () => Date }).toDate();
        if (v instanceof Date) return v;
        return null;
      };

      const expiry = toDate(d.expirationDate);
      const isPilot = !!d.isPilot;
      const paymentFailures = Number(d.paymentFailures) || 0;
      const now = new Date();

      // No expiry set → treat as active (admin-created orgs, legacy).
      if (!expiry) {
        emit({ status: 'active', daysLeft: 9999, daysInGrace: 0, graceEnds: null, expirationDate: null, isPilot });
        return;
      }

      const msLeft = expiry.getTime() - now.getTime();
      const daysLeft = Math.floor(msLeft / (1000 * 60 * 60 * 24));

      if (daysLeft >= 0) {
        // Still active — show a warning banner in the last 7 days.
        emit({ status: daysLeft <= 7 ? 'grace' : 'active', daysLeft, daysInGrace: 0, graceEnds: null, expirationDate: expiry, isPilot });
        return;
      }

      // Past expiry — compute or use stored gracePeriodUntil.
      const storedGrace = toDate(d.gracePeriodUntil);
      const graceEnds = storedGrace ?? new Date(expiry.getTime() + GRACE_DAYS * 24 * 60 * 60 * 1000);
      const daysInGrace = Math.floor((now.getTime() - expiry.getTime()) / (1000 * 60 * 60 * 24));

      if (now <= graceEnds) {
        // In grace AND a charge failed → surface the retry/M-Pesa banner.
        emit({ status: 'grace', daysLeft, daysInGrace, graceEnds, expirationDate: expiry, isPilot }, paymentFailures > 0);
      } else {
        emit({ status: 'locked', daysLeft, daysInGrace, graceEnds, expirationDate: expiry, isPilot });
      }
    });
    return () => unsub();
  }, [orgCode]);

  return sub;
}
